import { useState, useEffect, useRef } from "react";
import { Globe, ChevronDown } from "lucide-react";
import { useStaticTranslation } from "@/lib/staticTranslations";
import { getTranslateLang } from "@/lib/translateUtils";
import {
  triggerGoogleTranslate,
  TRANSLATE_LANGUAGES,
} from "@/components/i18n/GoogleTranslateSwitcher";

const openAccessibility = () => {
  const uw = (window as any).UserWay;
  if (uw?.widgetOpen) uw.widgetOpen();
};

export const HeaderTopBar = () => {
  const { t: th } = useStaticTranslation("header");
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getTranslateLang);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (code: string) => {
    setCurrentLang(code);
    setIsLangOpen(false);
    triggerGoogleTranslate(code);
  };

  return (
    <div className="w-full bg-muted border-b border-border/40">
      <div className="px-4 lg:px-8 h-8 w-full flex items-center justify-start ltr:justify-end rtl:justify-start md:justify-start md:ltr:justify-start md:rtl:justify-end gap-4 text-xs text-muted-foreground">
        <button
          type="button"
          onClick={openAccessibility}
          className="flex items-center gap-1.5 hover:text-foreground transition-colors"
        >
          <svg
            aria-hidden="true"
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M7 9L12 10M17 9L12 10M12 10V13M12 13L10 18M12 13L14 18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 7C11.7239 7 11.5 6.77614 11.5 6.5C11.5 6.22386 11.7239 6 12 6C12.2761 6 12.5 6.22386 12.5 6.5C12.5 6.77614 12.2761 7 12 7Z"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span>{th("accessibility", "נגישות")}</span>
        </button>

        <div ref={langRef} className="relative">
          <button
            type="button"
            onClick={() => setIsLangOpen((prev) => !prev)}
            aria-expanded={isLangOpen}
            className="flex items-center gap-1.5 hover:text-foreground transition-colors"
          >
            <Globe className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span>{th("language", "שפה")}</span>
            <ChevronDown
              className={`h-3 w-3 transition-transform ${isLangOpen ? "rotate-180" : ""}`}
              strokeWidth={1.5}
            />
          </button>

          {isLangOpen && (
            <div
              className="absolute end-0 top-full mt-1 bg-background border border-border rounded-lg shadow-lg z-[60] min-w-[150px] py-1 overflow-hidden notranslate"
              translate="no"
            >
              {TRANSLATE_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => handleSelect(lang.code)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-muted/80 ${
                    currentLang === lang.code
                      ? "bg-primary/10 font-semibold text-primary"
                      : "text-foreground"
                  }`}
                >
                  <img
                    src={lang.flag}
                    alt={lang.label}
                    className="w-6 h-4 object-cover rounded-sm border border-border/30"
                  />
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
